import { createFileRoute } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { useCallback, useEffect, useRef, useState } from "react";
import { MarketingShell, MarketingHero } from "@/components/MarketingShell";
import { PageContainer } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Phone, Loader2, CheckCircle2, LifeBuoy } from "lucide-react";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
  head: () => ({
    meta: [
      { title: "Contact MissionReady — Talk to our security ops team" },
      { name: "description", content: "Get in touch with MissionReady. Ask about pricing, migrations, or book a demo of our security guard management platform." },
      ...socialMeta({
        title: "Contact MissionReady",
        description: "Talk to the MissionReady team about pricing, migrations, or a live demo.",
        url: "https://missionready.dev/contact",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/contact" }],
    scripts: [...ORG_WEBSITE_JSONLD],
  }),
});

type Challenge = { id: string; question: string; token: string; expiresAt: number };

function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const loadedAtRef = useRef<number>(Date.now());
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [challengeError, setChallengeError] = useState(false);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  // Validation messages rendered next to the field they belong to, so screen
  // readers announce them through aria-describedby rather than a toast only.
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; email?: string; message?: string }>({});
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
    website: "", // honeypot — must stay empty
  });

  const loadChallenge = useCallback(async () => {
    setChallengeError(false);
    setChallenge(null);
    setCaptchaAnswer("");
    try {
      const res = await fetch("/api/public/contact-challenge", { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error("challenge failed");
      setChallenge((await res.json()) as Challenge);
    } catch {
      setChallengeError(true);
    }
  }, []);

  useEffect(() => {
    void loadChallenge();
  }, [loadChallenge]);

  function update<K extends keyof typeof form>(k: K, v: string) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const errors: { name?: string; email?: string; message?: string } = {};
    if (!form.name.trim()) errors.name = "Enter your name.";
    if (!form.email.trim()) errors.email = "Enter an email address we can reply to.";
    if (!form.message.trim()) errors.message = "Tell us what you need help with.";
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (form.message.trim().length > 4000) {
      toast.error("Message is too long (max 4000 characters).");
      return;
    }
    if (!challenge) {
      toast.error("Human verification isn't ready yet — please refresh and try again.");
      return;
    }
    if (!captchaAnswer.trim()) {
      toast.error("Please answer the verification question.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          loadedAt: loadedAtRef.current,
          captchaId: challenge.id,
          captchaToken: challenge.token,
          captchaExpiresAt: challenge.expiresAt,
          captchaAnswer: captchaAnswer.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.captcha) void loadChallenge();
        throw new Error(data?.error || "Failed to send message");
      }
      setSent(true);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <MarketingShell>
      <MarketingHero
        eyebrow="Contact"
        title={<>Talk to the <span className="text-primary">MissionReady team</span></>}
        subtitle="Questions about pricing, migrations, or a live demo? Send us a note and we'll reply within one business day."
      />

      <section className="py-16 md:py-20">
        <PageContainer>
          <div className="mx-auto grid max-w-5xl gap-10 md:grid-cols-[1fr_1.2fr]">
            <div className="space-y-6">
              <div>
                <h2 className="font-display text-2xl font-semibold">Get in touch</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Reach us any time. We typically respond within one business day.
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-start gap-3">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <LifeBuoy className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Support</div>
                    <a href="mailto:support@missionready.dev" className="mt-1 block font-medium hover:text-primary">
                      support@missionready.dev
                    </a>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Product help, account issues, and billing questions. One business day.
                    </p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-start gap-3">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sales</div>
                    <a href="mailto:sales@missionready.dev" className="mt-1 block font-medium hover:text-primary">
                      sales@missionready.dev
                    </a>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-start gap-3">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Phone</div>
                    <a href="tel:+15034646006" className="mt-1 block font-medium hover:text-primary">
                      (503) 464-6006
                    </a>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <a href="tel:+15034646006" className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/15">
                        <Phone className="h-3.5 w-3.5" /> Call
                      </a>
                      <a href="sms:+15034646006" className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/15">
                        <Phone className="h-3.5 w-3.5" /> Text
                      </a>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">Call or text — we reply during business hours.</p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">What to include</p>
                <ul className="mt-3 space-y-2 list-disc pl-5">
                  <li>Your team size (guards / sites)</li>
                  <li>Current software (if any)</li>
                  <li>What you're trying to solve</li>
                </ul>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
              {sent ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <CheckCircle2 className="h-7 w-7" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-semibold">Message sent</h3>
                  <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                    Thanks for reaching out. We've sent a confirmation to your inbox and will reply within one business day.
                  </p>
                  <Button
                    className="mt-6"
                    variant="outline"
                    onClick={() => {
                      setSent(false);
                      setForm({ name: "", email: "", company: "", phone: "", message: "", website: "" });
                      loadedAtRef.current = Date.now();
                      void loadChallenge();
                    }}
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4">
                  {/* Honeypot — hidden from users, catches bots that auto-fill every field. */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                    <label htmlFor="website">Website</label>
                    <input
                      id="website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={(e) => update("website", e.target.value)}
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="name">Name *</Label>
                      {fieldErrors.name && (
                        <p id="name-err" className="text-xs font-medium text-destructive">{fieldErrors.name}</p>
                      )}
                      <Input id="name" required autoComplete="name" aria-invalid={!!fieldErrors.name} aria-describedby={fieldErrors.name ? "name-err" : undefined} maxLength={120} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="email">Email *</Label>
                      {fieldErrors.email && (
                        <p id="email-err" className="text-xs font-medium text-destructive">{fieldErrors.email}</p>
                      )}
                      <Input id="email" type="email" required autoComplete="email" aria-invalid={!!fieldErrors.email} aria-describedby={fieldErrors.email ? "email-err" : undefined} maxLength={255} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" />
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="company">Company</Label>
                      <Input id="company" autoComplete="organization" maxLength={200} value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Acme Security" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" type="tel" autoComplete="tel" maxLength={50} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(optional)" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="message">Message *</Label>
                    {fieldErrors.message && (
                      <p id="message-err" className="text-xs font-medium text-destructive">{fieldErrors.message}</p>
                    )}
                    <Textarea
                      id="message"
                      required
                      aria-invalid={!!fieldErrors.message}
                      aria-describedby={fieldErrors.message ? "message-err" : undefined}
                      rows={6}
                      maxLength={4000}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Tell us about your team and what you're looking for."
                    />
                    <div className="text-right text-xs text-muted-foreground">{form.message.length}/4000</div>
                  </div>
                  {/* Human verification — signed, single-use challenge issued by the server. */}
                  <div className="space-y-1.5 rounded-xl border border-border bg-muted/30 p-4">
                    <Label htmlFor="captcha">Human verification *</Label>
                    {challengeError ? (
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>Couldn't load the verification question.</span>
                        <button type="button" onClick={() => void loadChallenge()} className="font-semibold text-primary hover:underline">
                          Retry
                        </button>
                      </div>
                    ) : challenge ? (
                      <>
                        <p className="text-sm text-muted-foreground">{challenge.question}</p>
                        <Input
                          id="captcha"
                          inputMode="numeric"
                          autoComplete="off"
                          maxLength={12}
                          required
                          value={captchaAnswer}
                          onChange={(e) => setCaptchaAnswer(e.target.value)}
                          placeholder="Your answer"
                          className="max-w-[180px]"
                        />
                      </>
                    ) : (
                      <p className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading verification…
                      </p>
                    )}
                  </div>
                  <Button type="submit" size="lg" className="w-full" disabled={submitting || !challenge}>
                    {submitting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                    ) : "Send message"}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    We'll only use your info to reply. No spam, ever.
                  </p>
                </form>
              )}
            </div>
          </div>
        </PageContainer>
      </section>
    </MarketingShell>
  );
}
